// Insurance A/R — mock data (INSURANCE_AR_HANDOFF §9, Oct 15 2026). UI only.
import type { ArClaim, ArTry, FollowUpSettings, WriteOffRow } from './types';
import { generateLateExtra, generateOntime } from './historyMock';
import { countRounds, nextCheckDate, scheduledLateTries } from './schedule';
import { AR_PAYERS, usualDaysFor, usuallyWords } from './payers';
import { IAR_SETTINGS_CHANGER, IAR_USER, isoLabel, isoPlusDays, money } from './utils';
import { PAYS_PATIENT_CLAIMS } from '@/features/networks/paidToPatient';
import { PMS } from '@/features/shared/pms';

// Group counts and totals (late, within the usual time, ELVA isn't following up, the header's
// total) are never typed here: the store sums them from the rows below (AGENT_GUIDE rule 1).

export const IAR_WRITTEN_OFF_TOTAL = 310.0;
export const IAR_WRITTEN_OFF_COUNT = 3;

// Age buckets are computed from the live claim records in the store
// (getBucketsSync), so the pills and the list they filter always agree.

export function buildWrittenOff(): WriteOffRow[] {
  return [
    {
      id: 'WO-ROSA',
      recordId: 'C-ROSA',
      dateDisplay: 'Oct 13',
      iso: '2026-10-13',
      patient: 'Rosa Kim',
      visit: 'visit Aug 31',
      payer: 'MetLife',
      why: null,
      by: `In ${PMS} by Maria Gomez`,
      amount: 80.0,
      fromPms: true,
    },
    {
      id: 'WO-MIA',
      recordId: 'C-MIA',
      dateDisplay: 'Oct 9',
      iso: '2026-10-09',
      patient: 'Mia Cole',
      visit: 'visit Jul 2, 2025',
      payer: 'Aetna',
      why: 'Filed too late: sent 14 months after the visit, before ELVA started',
      by: 'Ava Thompson · Lone Star Billing',
      amount: 188.0,
    },
    {
      id: 'WO-HUGO',
      recordId: 'C-HUGO',
      dateDisplay: 'Oct 6',
      iso: '2026-10-06',
      patient: 'Hugo Lane',
      visit: 'visit Sep 11',
      payer: 'Delta Dental of Texas',
      why: 'Small balance, not worth chasing',
      by: 'Ava Thompson · Lone Star Billing',
      amount: 42.0,
    },
  ];
}

export function buildSettings(): FollowUpSettings {
  return {
    limit: 3,
    // D165: a billing company's staff can't change practice settings — the
    // changer is a practice owner/admin (staging fix), not Lone Star Billing.
    changedBy: `Changed by ${IAR_SETTINGS_CHANGER} Oct 1. Applies to claims already waiting, too.`,
    // One table for the whole feature (payers.ts): the same usual times decide when a claim is late.
    payerTimes: AR_PAYERS.flatMap((p) => {
      const d = usualDaysFor(p.name);
      return d === undefined ? [] : [{ payer: p.name, usually: usuallyWords(d) }];
    }),
  };
}

/* ---- the claims ---- */

export function buildClaims(): ArClaim[] {
  const late: ArClaim[] = [
    {
      id: 'AR-ELENA',
      patient: 'Elena Ruiz',
      firstName: 'Elena',
      payer: { name: 'BlueCross BlueShield of Texas', short: 'BCBS' },
      memberId: 'XGT302118856',
      dentist: 'Dr. Emily Carter',
      procedure: 'Crown on #14 (D2740)',
      visit: 'visit Sep 2',
      sentDisplay: 'Sep 4',
      receivedDisplay: 'received by BCBS Sep 4',
      billed: 1028.0,
      balance: 1028.0,
      group: 'late',
      days: 41,
      serviceDays: 43,
      sentIso: '2026-09-04',
      // Fee $1,285.00; ELVA expects $1,028.00 from BCBS; Elena's own share is $257.00; the PMS shows the same $1,028.00 pending.
      money: { pmsPending: 1028.0, elvaExpected: 1028.0, patientShare: 257.0 },
      round: { n: 2, limit: 3 },
      status: {
        kind: 'late',
        headline: 'BCBS hasn’t paid in 41 days. It usually pays in about 3 weeks.',
        latest:
          'BCBS asked for the X-ray on Oct 12. ELVA sent it through the BCBS portal the same day. BCBS says it will review within 10 business days.',
        next: 'ELVA checks again on Oct 23. That’s follow-up 3 of 3. If it still isn’t paid, it goes to Human in the loop.',
        deadline: 'No filing deadline at risk: BCBS received the claim on Sep 4.',
      },
      rowBadge: 'late',
      rowLine: 'BCBS asked for the X-ray; ELVA sent it Oct 12',
      rowMeta: '43 days by date of service · 41 days since sent · follow-up 2 of 3 · next check Oct 23',
      nextCheck: 'Oct 23',
      // Rounds are 14 days apart: Sep 25, Oct 9, Oct 23. Late since Sep 25 (BCBS usually pays in about 3 weeks).
      tries: [
        { date: 'Sep 4', icon: 'ok', head: 'Sent.', body: 'Accepted by the clearinghouse. BCBS received it the same day.', logId: 'ar-elena-1' },
        { date: 'Sep 25', icon: 'dot', head: 'Follow-up 1 · status check.', body: 'BCBS: “in process”. Nothing missing.', logId: 'ar-elena-2' },
        { date: 'Oct 9', icon: 'x', head: 'Follow-up 2 · status check.', body: 'Still “in process”, now past BCBS’s usual time.', logId: 'ar-elena-3' },
        { date: 'Oct 9', icon: 'x', head: 'Follow-up 2 · BCBS portal.', body: '“Pending review”, no reason shown.', logId: 'ar-elena-4' },
        { date: 'Oct 9', icon: 'x', head: 'Follow-up 2 · call to BCBS.', body: 'On hold 47 minutes, then the line closed.', logId: 'ar-elena-5' },
        { date: 'Oct 12', icon: 'ok', head: 'Follow-up 2 · call to BCBS.', body: 'Rep asked for the X-ray of #14. Reference BC-77120-4.', logId: 'ar-elena-6', recordingId: 'rec-ar-elena-6' },
        { date: 'Oct 12', icon: 'ok', head: 'Follow-up 2 · BCBS portal.', body: 'Uploaded the X-ray of Sep 2 to the claim.', logId: 'ar-elena-7' },
      ],
      history:
        'History: claim confirmed by Ava Thompson (Lone Star Billing) Sep 4 · sent by ELVA Sep 4 · follow-ups by ELVA Sep 25, Oct 9–12',
    },
    {
      id: 'AR-TOM',
      patient: 'Tom Becker',
      firstName: 'Tom',
      payer: { name: 'MetLife', short: 'MetLife' },
      memberId: '553-44-1290-01',
      dentist: 'Dr. Emily Carter',
      procedure: 'Filling on #30 (D2392)',
      visit: 'visit Sep 15',
      sentDisplay: 'Sep 16',
      receivedDisplay: 'received by MetLife Sep 16',
      billed: 386.0,
      balance: 386.0,
      group: 'late',
      days: 29,
      serviceDays: 30,
      sentIso: '2026-09-16',
      round: { n: 1, limit: 3 },
      status: {
        kind: 'handled',
        headline: 'MetLife had no record of the claim, so ELVA sent it again',
        bodyLines: ['Next check Oct 23. The filing deadline isn’t at risk: the first report proves it was filed on time.'],
      },
      rowBadge: 'late',
      rowLine: 'MetLife had no record; ELVA resent it Oct 9',
      rowMeta: '30 days by date of service · 29 days since sent · follow-up 1 of 3 · next check Oct 23',
      nextCheck: 'Oct 23',
      tries: [
        { date: 'Sep 16', icon: 'ok', head: 'Sent.', body: 'Accepted by the clearinghouse. MetLife accepted it the same day.', logId: 'ar-tom-1' },
        { date: 'Oct 7', icon: 'x', head: 'Follow-up 1 · status check.', body: 'MetLife: “claim not found”.', logId: 'ar-tom-2' },
        { date: 'Oct 8', icon: 'x', head: 'Follow-up 1 · MetLife portal.', body: 'No claim for Sep 15.', logId: 'ar-tom-3' },
        { date: 'Oct 9', icon: 'ok', head: 'Resent.', body: 'Sent again with the clearinghouse report showing MetLife accepted it Sep 16. Accepted again.', logId: 'ar-tom-4' },
      ],
      history: 'History: confirmed and sent by ELVA Sep 16 · resent by ELVA Oct 9',
    },
    {
      id: 'AR-AISHA',
      patient: 'Aisha Patel',
      firstName: 'Aisha',
      payer: { name: 'Cigna', short: 'Cigna' },
      memberId: 'CIG50418822',
      dentist: 'Dr. Raymond Poe',
      procedure: 'Scaling, one quadrant (D4342)',
      visit: 'visit Sep 20',
      sentDisplay: 'Sep 22',
      receivedDisplay: 'received by Cigna Sep 22',
      billed: 210.0,
      balance: 210.0,
      group: 'late',
      days: 23,
      serviceDays: 25,
      sentIso: '2026-09-22',
      // One round really ran (status check, then the call Cigna refused). The
      // count is read from the tries below, so it reads "Follow-up 1 of 3", not
      // "3 of 3" (staging bug). She's in Human in the loop because Cigna wants
      // office staff, not because she hit the limit.
      round: { n: 1, limit: 3 },
      hitlItemId: 'HL-CALL', // the real item in features/human-in-the-loop
      status: {
        kind: 'limit',
        headline: 'Cigna wants to speak with office staff. It’s in Human in the loop.',
        bodyLines: [
          'Cigna won’t talk to ELVA about this claim and asked for someone from the office to call. 23 days since it was sent.',
          'In Human in the loop, someone makes the call or decides to stop and write it off.',
        ],
      },
      rowBadge: 'needs',
      rowLine: 'Cigna wants to speak with office staff',
      rowMeta: '25 days by date of service · 23 days since sent · in Human in the loop',
      tries: [
        { date: 'Sep 22', icon: 'ok', head: 'Sent.', body: 'Accepted by the clearinghouse. Cigna received it the same day.', logId: 'ar-aisha-1' },
        { date: 'Oct 6', icon: 'dot', head: 'Follow-up 1 · status check.', body: 'Cigna: “in process”.', logId: 'ar-aisha-2' },
        { date: 'Oct 13', icon: 'x', head: 'Follow-up 1 · call to Cigna.', body: 'Cigna will only speak with office staff about this claim.', logId: 'ar-aisha-3' },
      ],
      history: 'History: sent by ELVA Sep 22 · follow-ups by ELVA Oct 6, Oct 13',
    },
    {
      id: 'AR-VICTOR',
      patient: 'Victor Lang',
      firstName: 'Victor',
      payer: { name: 'United Concordia', short: 'United Concordia' },
      memberId: 'UC88120473',
      dentist: 'Dr. Raymond Poe',
      procedure: 'Deep cleaning, 2 quadrants (D4341)',
      visit: 'visit Jul 20',
      sentDisplay: 'Jul 21',
      receivedDisplay: 'received by United Concordia Jul 21',
      billed: 604.0,
      balance: 604.0,
      group: 'late',
      days: 86,
      serviceDays: 87,
      sentIso: '2026-07-21',
      // Dentrix shows $640.00 pending for it; ELVA expected $604.00. (`?scenario=line-under-appeal`
      // adds the "another line is under appeal" notice in the store.)
      money: { pmsPending: 640.0, elvaExpected: 604.0, patientShare: 151.0 },
      round: { n: 3, limit: 3 },
      // At the limit (3 of 3): the store links his real Human in the loop item
      // (HL-VICTOR) and writes the "Needs a person" texts — see store.applyLimit.
      status: {
        kind: 'limit',
        headline: 'ELVA followed up 3 times, the limit you set. It’s in Human in the loop.',
        bodyLines: [
          'United Concordia keeps saying “in review” and gives no date. 86 days since the claim was sent.',
          'In Human in the loop, someone decides: let ELVA keep going (3 more follow-ups), or stop and write it off.',
        ],
      },
      rowBadge: 'needs',
      rowLine: 'ELVA followed up 3 times; it’s in Human in the loop',
      rowMeta: '87 days by date of service · 86 days since sent · in Human in the loop',
      tries: [
        { date: 'Jul 21', icon: 'ok', head: 'Sent.', body: 'Accepted by the clearinghouse. United Concordia received it the same day.', logId: 'ar-victor-1' },
        { date: 'Aug 6', icon: 'dot', head: 'Follow-up 1 · status check.', body: 'United Concordia: “in review”.', logId: 'ar-victor-2' },
        { date: 'Aug 22', icon: 'x', head: 'Follow-up 2 · status check.', body: 'Still “in review”, no date given.', logId: 'ar-victor-3' },
        { date: 'Sep 12', icon: 'x', head: 'Follow-up 3 · call to United Concordia.', body: '“In review”, escalated. Reference UC-55210-9.', logId: 'ar-victor-4', recordingId: 'rec-ar-victor-4' },
      ],
      history: 'History: sent by ELVA Jul 21 · follow-ups by ELVA Aug 6 – Sep 12',
    },
    ...generatedLate(),
    ...generateLateExtra(),
  ];

  const ontime: ArClaim[] = [
    ontimeClaim('AR-OWEN', 'Owen Fischer', 'Owen', 'Delta Dental of Texas', 'Delta', '81553017', 164.0, 'Oct 6',
      'Cleaning and exam', 'visit Oct 5', 9, 'Received by Delta Oct 7, in process',
      '9 days since sent · Delta usually pays in about 2 weeks',
      'Received by Delta on Oct 7 and in process.',
      'Delta usually pays in about 2 weeks. ELVA checks on Oct 20 if it hasn’t been paid by then.', 'Oct 7',
      '2026-10-06', 10),
    ontimeClaim('AR-LILY', 'Lily Tran', 'Lily', 'Aetna', 'Aetna', 'AET72014508', 612.0, 'Oct 8',
      'Crown on #19 (D2750)', 'visit Oct 7', 7, 'Received by Aetna Oct 9, in process',
      '7 days since sent · Aetna usually pays in about 3 weeks',
      'Received by Aetna on Oct 9 and in process.',
      'Aetna usually pays in about 3 weeks. ELVA checks on Oct 29 if it hasn’t been paid by then.', 'Oct 9',
      '2026-10-08', 8),
    ontimeClaim('AR-KARA', 'Kara Singh', 'Kara', 'Guardian', 'Guardian', 'GRD40188271', 1015.0, 'Oct 12',
      'Root canal, molar (D3330)', 'visit Oct 11', 3, 'Accepted by the clearinghouse',
      '3 days since sent · first check Oct 26',
      'Accepted by the clearinghouse on Oct 12.',
      'Guardian usually pays in about 2 weeks. ELVA’s first check is Oct 26.', null,
      '2026-10-12', 4),
    ...generateOntime(),
  ];

  // ELVA isn't following up (Oct 22): these two moved out of "Within the payer's usual time".
  // They are still owed, so the header, aging cards and payers count them.
  const notFollowed: ArClaim[] = [noahPark(), graceLiu()];

  const all = [...late, ...ontime, ...notFollowed, ...buildPaysPatientClaims()];
  for (const c of all) {
    // The payer's usual time comes from one table (payers.ts). A payer without one (Sun Life in
    // the mock) has none: its claims can only be late by the unpaid threshold or a state deadline.
    if (c.usualDays === undefined) {
      const d = usualDaysFor(c.payer.name);
      if (d !== undefined) c.usualDays = d;
    }
  }
  // The round count is the number of rounds that really ran, read from the
  // tries — never a hardcoded number that can drift past them (staging bug:
  // "Follow-up 3 of 3 after one round"). Capped at the limit, so it can never
  // read "4 of 3".
  for (const c of all) {
    if (c.round) {
      const ran = countRounds(c.tries);
      if (ran > 0) {
        c.ran = ran;
        c.round = { n: Math.min(ran, c.round.limit), limit: c.round.limit };
      }
    }
  }
  return all;
}

function ontimeClaim(
  id: string, patient: string, firstName: string, payerName: string, short: string,
  memberId: string, billed: number, sent: string, procedure: string, visit: string,
  days: number, rowLine: string, rowMeta: string, headline: string, sub: string,
  received: string | null,
  sentIso: string,
  serviceDays: number,
): ArClaim {
  const tries: ArTry[] = [
    { date: sent, icon: 'ok', head: 'Sent.', body: 'Accepted by the clearinghouse.', logId: `${id.toLowerCase()}-1` },
  ];
  if (received) {
    tries.push({ date: received, icon: 'dot', head: 'Received.', body: `${short} has the claim and is processing it.`, logId: `${id.toLowerCase()}-2` });
  }
  return {
    id, patient, firstName,
    payer: { name: payerName, short },
    memberId, dentist: 'Dr. Emily Carter',
    procedure, visit, sentDisplay: sent,
    receivedDisplay: received ? `received by ${short} ${received}` : `sent ${sent}`,
    billed, balance: billed, group: 'ontime', days, serviceDays, sentIso,
    status: { kind: 'ontime', headline, bodyLines: [sub] },
    rowBadge: 'none', rowLine, rowMeta,
    tries,
    history: `History: sent by ELVA ${sent}`,
  };
}

/** Noah Park: Guardian, $680.00, D2750 #3, sent Sep 30. Paused by Ava Thompson on Oct 13 until Nov 21. */
function noahPark(): ArClaim {
  const base = ontimeClaim('AR-NOAH', 'Noah Park', 'Noah', 'Guardian', 'Guardian', 'GRD58204417', 680.0, 'Sep 30',
    'Crown on #3 (D2750)', 'visit Sep 29', 15, 'Received by Guardian Oct 1, in process',
    '15 days since sent · Guardian usually pays in about 2 weeks',
    'Received by Guardian on Oct 1 and in process.',
    'Guardian usually pays in about 2 weeks.', 'Oct 1',
    '2026-09-30', 16);
  const note = 'waiting for the patient’s new card';
  const until = 'Nov 21';
  const nextCheck = 'Nov 23';
  const line = `Paused by ${IAR_USER.name} until ${until} · ${note}`;
  return {
    ...base,
    group: 'not-followed',
    stopped: {
      kind: 'paused',
      by: IAR_USER.name,
      on: 'Oct 13',
      onIso: '2026-10-13',
      until,
      nextCheck,
      note,
      line,
      prior: {
        group: 'ontime',
        status: base.status,
        rowBadge: base.rowBadge,
        rowLine: base.rowLine,
        rowMeta: base.rowMeta,
      },
    },
    status: {
      kind: 'paused',
      headline: `${line}. ELVA checks again on ${nextCheck}.`,
    },
    rowBadge: 'paused',
    rowLine: line,
    rowMeta: '16 days by date of service · 15 days since sent',
    nextCheck,
    history: 'History: sent by ELVA Sep 30 · paused by Ava Thompson (Lone Star Billing) Oct 13',
  };
}

/** Grace Liu: Delta Dental of Texas, $460.00, D2392 #19, sent Oct 2. Billed to MetLife, says Maria Gomez, Oct 14. */
function graceLiu(): ArClaim {
  const base = ontimeClaim('AR-GRACELIU', 'Grace Liu', 'Grace', 'Delta Dental of Texas', 'Delta', '81553902', 460.0, 'Oct 2',
    'Filling on #19 (D2392)', 'visit Oct 1', 13, 'Received by Delta Oct 3, in process',
    '13 days since sent · Delta usually pays in about 2 weeks',
    'Received by Delta on Oct 3 and in process.',
    'Delta usually pays in about 2 weeks.', 'Oct 3',
    '2026-10-02', 14);
  const line = 'Billed to MetLife, says Maria Gomez · ELVA hasn’t found that claim yet';
  return {
    ...base,
    group: 'not-followed',
    stopped: {
      kind: 'billed_elsewhere',
      by: 'Maria Gomez',
      on: 'Oct 14',
      onIso: '2026-10-14',
      otherPayer: 'MetLife',
      line,
      prior: {
        group: 'ontime',
        status: base.status,
        rowBadge: base.rowBadge,
        rowLine: base.rowLine,
        rowMeta: base.rowMeta,
      },
    },
    status: {
      kind: 'billed_elsewhere',
      headline: 'Billed to MetLife, says Maria Gomez.',
      bodyLines: [`ELVA hasn’t found that claim yet, so ${base.payer.short}’s ${money(base.balance)} still counts as owed. ELVA looks again on every sync.`],
    },
    rowBadge: 'not-following',
    rowLine: line,
    rowMeta: '14 days by date of service · 13 days since sent',
    history: 'History: sent by ELVA Oct 2 · billed to MetLife, says Maria Gomez, Oct 14',
  };
}

/** The 5 generated late claims (31–120 days). Sum = $4,662.40 so late total = $6,890.40. */
function generatedLate(): ArClaim[] {
  const specs: [string, string, string, string, string, number, string, number, string][] = [
    // id, name, payerName, short, memberId, billed, sent, days since sent, sent ISO
    ['AR-GRACE', 'Grace Howard', 'Delta Dental of Texas', 'Delta', 'DDX60421190', 1284.0, 'Aug 24', 52, '2026-08-24'],
    ['AR-MARCUS', 'Marcus Webb', 'Aetna', 'Aetna', 'AET33910274', 890.4, 'Aug 13', 63, '2026-08-13'],
    ['AR-NINA', 'Nina Alvarez', 'Guardian', 'Guardian', 'GRD71820094', 742.0, 'Aug 5', 71, '2026-08-05'],
    ['AR-DEREK', 'Derek Olsen', 'Cigna', 'Cigna', 'CIG20947713', 980.0, 'Aug 28', 48, '2026-08-28'],
    ['AR-SANA', 'Sana Malik', 'MetLife', 'MetLife', '553-90-2217-02', 766.0, 'Jul 12', 95, '2026-07-12'],
  ];
  const LIMIT = 3;
  return specs.map(([id, patient, payerName, short, memberId, billed, sent, days, sentIso]) => {
    const serviceDays = days + 2;
    // D308: the patient can be asked to pay only when the payer has said what she owes, or the
    // dentist is confirmed out of network with that payer.
    const patientPaysBasis =
      id === 'AR-DEREK' ? ('payer_decided' as const) : id === 'AR-NINA' ? ('out_of_network' as const) : undefined;
    const first = patient.split(' ')[0] ?? patient;
    // Sana is the one generated claim already at the limit (HL-SANA exists).
    const round = id === 'AR-SANA' ? 3 : days > 55 ? 2 : 1;
    const deadline = `No filing deadline at risk: ${short} received the claim on ${sent}.`;
    // Every round that ran shows as a dated try on the real schedule (§7).
    const tries = scheduledLateTries(id.toLowerCase(), short, sent, round, days);
    const base = {
      id,
      patient,
      firstName: first,
      payer: { name: payerName, short },
      memberId,
      dentist: 'Dr. Raymond Poe',
      procedure: 'Treatment claim',
      visit: `visit ${isoLabel(isoPlusDays(sentIso, -2))}`,
      sentDisplay: sent,
      receivedDisplay: `received by ${short} ${sent}`,
      billed,
      balance: billed,
      group: 'late' as const,
      days,
      serviceDays,
      sentIso,
      ...(patientPaysBasis ? { patientPaysBasis } : {}),
      round: { n: round, limit: LIMIT },
      tries,
      history: `History: sent by ELVA ${sent} · follow-ups by ELVA`,
    };

    const next = nextCheckDate(days);
    if (round >= LIMIT) {
      // At the limit: the store (applyLimit) writes the Needs-a-person texts and
      // links the claim's real Human in the loop item.
      return {
        ...base,
        status: { kind: 'late' as const, headline: '', deadline },
        rowBadge: 'late' as const,
        rowLine: '',
        rowMeta: '',
      };
    }

    return {
      ...base,
      nextCheck: next,
      status: {
        kind: 'late' as const,
        headline: `${short} hasn’t paid in ${String(days)} days. It’s past ${short}’s usual time.`,
        latest: `${short} says the claim is still in process. ELVA has been following up.`,
        next: `ELVA checks again on ${next}. That’s follow-up ${String(round + 1)} of ${String(LIMIT)}.`,
        deadline,
      },
      rowBadge: 'late' as const,
      rowLine: `${short} is past its usual time; ELVA is following up`,
      rowMeta: `${String(serviceDays)} days by date of service · ${String(days)} days since sent · follow-up ${String(round)} of ${String(LIMIT)} · next check ${next}`,
    };
  });
}

/* ---- claims where the payer pays the patient (D197, H3) ---- */

/** Days are counted to Oct 15, the day the board was drawn (13 days for a claim sent Oct 2). */
const PAYS_ANCHOR = Date.UTC(2026, 9, 15);

interface PaysPatientDetail {
  memberId: string;
  procedure: string;
  visit: string;
}
const HE = new Set(['Leo', 'Cole', 'Ray']);
const PAYS_PATIENT_DETAIL: Record<string, PaysPatientDetail> = {
  'CLM-28822': { memberId: '553-18-0442-01', procedure: 'Root canal on #30 (D3330)', visit: 'visit Oct 1' },
  'CLM-28820': { memberId: '553-27-1130-01', procedure: 'Crown on #19 (D2740)', visit: 'visit Sep 30' },
  'CLM-28818': { memberId: 'GU-4410-8821', procedure: 'Crown on #30 (D2740)', visit: 'visit Sep 27' },
  'CLM-28812': { memberId: '553-61-0097-02', procedure: 'Root canal on #3 (D3330)', visit: 'visit Sep 21' },
  'CLM-28806': { memberId: 'GU-5532-1907', procedure: 'Crown on #14 (D2740)', visit: 'visit Sep 17' },
  'CLM-28798': { memberId: 'GU-2208-6643', procedure: 'Crown on #3 (D2740)', visit: 'visit Sep 9' },
};

/** The six claims of "The payer pays the patient". Built from the shared list, so the group's count and money are recomputed, never typed. */
export function buildPaysPatientClaims(): ArClaim[] {
  return PAYS_PATIENT_CLAIMS.map((p): ArClaim => {
    const [y, m, d] = p.sentIso.split('-').map(Number);
    const days = Math.round((PAYS_ANCHOR - Date.UTC(y ?? 2026, (m ?? 1) - 1, d ?? 1)) / 86400000);
    const first = p.patient.split(' ')[0] ?? p.patient;
    const detail = PAYS_PATIENT_DETAIL[p.claimId];
    const lower = p.claimId.toLowerCase();
    return {
      id: p.claimId,
      patient: p.patient,
      firstName: first,
      payer: { name: p.payer, short: p.payer },
      memberId: detail?.memberId ?? '',
      dentist: 'Dr. Marcus Lee',
      procedure: detail?.procedure ?? 'Treatment claim',
      visit: detail?.visit ?? `visit ${p.sent}`,
      sentDisplay: p.sent,
      receivedDisplay: `received by ${p.payer} ${p.sent}`,
      billed: p.billed,
      balance: p.billed,
      group: 'pays-patient',
      days,
      sentIso: p.sentIso,
      status: {
        kind: 'pays-patient',
        headline:
          days <= 14
            ? `${p.payer} usually pays in about 2 weeks. ${String(days)} days so far.`
            : `${p.payer} has had the claim for ${String(days)} days.`,
        latest: `${p.payer} says the claim is in process.`,
        bodyLines: [`When ${p.payer} pays ${first}, the claim closes as Paid to the patient and ${first}’s balance shows what ${HE.has(first) ? 'he' : 'she'} owes the practice.`],
      },
      rowBadge: 'pays-patient',
      rowLine: '',
      rowMeta: `${String(days)} days since sent · within ${p.payer}’s usual time`,
      tries: [
        { date: p.sent, icon: 'ok', head: 'Sent.', body: `Accepted by the clearinghouse. ${p.payer} received it the same day.`, logId: `${lower}-1` },
      ],
      history: `History: sent by ELVA ${p.sent} · ${p.payer} pays the patient, so ELVA follows the claim to know when the patient has been paid`,
    };
  });
}
